'use client';

import classnames from 'classnames';
import { useState } from 'react';
import { Container, Reveal } from '@stadionhq-v3/base-ui';
import { Plus } from '@components/icons';
import VideoCard from '@components/v3/molecules/video-cards';
import { Locale } from '@core/constants/locales.constants';
import { CleanContentful } from '@stadionhq-v3/clean-contentful';
import { Video } from '@graphql/index';
import { Translations } from '@lib/dictionary-keys/__generated__';
import { videoCardTransform } from '@utils/transforms';
import { PLAYLIST_BATCH_SIZE } from '@core/constants/video.constants';
import { InfiniteLoader } from '@components/layout/infinite-loader';
import styles from '../videoPlaylistTemplate.module.scss';

type YouTubeVideoPlaylistProps = {
  items: CleanContentful<Video>[];
  locale: Locale;
  playlistSlug: string;
  dictionary: Pick<Translations['common']['action'], 'loadMore'> &
    Pick<Translations['videoCard']['label'], 'membersOnly'>;
};

const YouTubeVideoPlaylist = ({
  items,
  locale,
  playlistSlug,
  dictionary,
}: YouTubeVideoPlaylistProps) => {
  const [shownCount, setShownCount] = useState(PLAYLIST_BATCH_SIZE);
  const visibleItems = items.slice(0, shownCount);
  const hasMore = shownCount < items.length;

  const loadMore = () => {
    setShownCount((c) => Math.min(c + PLAYLIST_BATCH_SIZE, items.length));
  };

  return (
    <InfiniteLoader
      render={() => (
        <Container classNames={[classnames(styles.content)]} as="ul">
          {visibleItems.map((item) => (
            <Reveal key={item.id} classNames={[classnames(styles.videoCard)]}>
              <VideoCard
                {...videoCardTransform(item, locale, {
                  playlistContext: { slug: playlistSlug },
                })}
                key={`video-card-${item.id}`}
              />
            </Reveal>
          ))}
        </Container>
      )}
      items={visibleItems}
      showLoadMoreButton={hasMore}
      loadMoreButton={{
        text: dictionary.loadMore,
        iconAfter: <Plus />,
        onClick: loadMore,
        variant: 'secondary',
        rounded: true,
        onDark: true,
      }}
    />
  );
};

export default YouTubeVideoPlaylist;
